import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { BLOG } from "../lib/data";

const fmt = (d) => new Date(d).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;
  const more = BLOG.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <>
      <Seo title={post.title} description={post.excerpt} path={`/blog/${slug}`} />

      <article className="pt-32 sm:pt-40 pb-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-navy font-medium text-sm hover:gap-3 transition-all" data-testid="back-to-blog">
            <ArrowLeft className="h-4 w-4" /> All articles
          </Link>
          <span className="mt-6 inline-block rounded-full bg-navy/5 text-navy px-3 py-1 text-xs font-semibold">{post.category}</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold text-navy tracking-tighter leading-tight">{post.title}</h1>
          <div className="mt-5 flex items-center gap-4 text-sm text-text-muted">
            <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" />{fmt(post.date)}</span>
            <span>·</span><span>{post.readTime}</span>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-5 sm:px-8 mt-10">
          <div className="overflow-hidden rounded-[2rem]">
            <img src={post.image} alt={post.title} className="w-full h-[300px] sm:h-[440px] object-cover" />
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-5 sm:px-8 mt-12">
          <div className="space-y-6">
            {post.content.map((para, i) => (
              <p key={i} className="text-lg text-text-muted leading-relaxed">{para}</p>
            ))}
          </div>

          <div className="mt-12 rounded-3xl bg-navy p-8 sm:p-10 text-center">
            <h3 className="font-display text-2xl font-bold text-white">Need help with an injury?</h3>
            <p className="mt-2 text-white/75">Book an assessment with our physiotherapists today.</p>
            <Link to="/book" className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 font-semibold text-navy hover:-translate-y-0.5 transition-all" data-testid="blogpost-book-button">
              Book Appointment <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </article>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <h2 className="font-display text-2xl font-semibold text-text-dark mb-8">Keep reading</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {more.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08}>
                <Link to={`/blog/${p.slug}`} className="group block bg-white rounded-3xl border border-border-light overflow-hidden card-hover" data-testid={`related-${p.slug}`}>
                  <div className="h-48 overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover img-zoom" />
                  </div>
                  <div className="p-7">
                    <h3 className="font-display text-lg font-semibold text-text-dark group-hover:text-navy transition-colors">{p.title}</h3>
                    <p className="mt-2 text-text-muted text-sm leading-relaxed">{p.excerpt}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
